/* eslint-disable @next/next/no-img-element -- favicon/external/data-URI; next/image migration impractical for these dynamic logo/signature sources */

import * as React from 'react';
import { cn } from '@/lib/utils';

interface InstitutionLogoProps {
  name: string;
  faviconUrl?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const sizeConfig = {
  sm: { height: 20 },
  md: { height: 24 },
  lg: { height: 32 }
};

// Commonwealth Bank SVG logo
const CommBankLogo = ({
  className,
  style
}: {
  className?: string;
  style?: React.CSSProperties;
}) => (
  <svg
    clipRule="evenodd"
    fillRule="evenodd"
    strokeLinejoin="round"
    strokeMiterlimit="1.41421"
    viewBox="0 0 270 270"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    style={style}
  >
    <linearGradient
      id="a"
      gradientTransform="matrix(375.632 315.127 -315.127 375.632 132.184 162.437)"
      gradientUnits="userSpaceOnUse"
      x1="0"
      x2="1"
      y1="0"
      y2="0"
    >
      <stop offset="0" stopColor="#ff0" />
      <stop offset=".27" stopColor="#fef10c" />
      <stop offset=".45" stopColor="#fde516" />
      <stop offset=".64" stopColor="#fde113" />
      <stop offset=".82" stopColor="#fed508" />
      <stop offset=".92" stopColor="#fc0" />
      <stop offset="1" stopColor="#fc0" />
    </linearGradient>
    <linearGradient
      id="b"
      gradientTransform="matrix(116.7 73 -73 116.7 389.275 407.639)"
      gradientUnits="userSpaceOnUse"
      x1="0"
      x2="1"
      y1="0"
      y2="0"
    >
      <stop offset="0" stopColor="#874400" />
      <stop offset=".1" stopColor="#874400" />
      <stop offset=".26" stopColor="#d88a00" />
      <stop offset=".37" stopColor="#e79d00" />
      <stop offset=".52" stopColor="#f2b400" />
      <stop offset=".7" stopColor="#fad202" />
      <stop offset=".9" stopColor="#fedf03" />
      <stop offset="1" stopColor="#fedf03" />
    </linearGradient>
    <g fillRule="nonzero" transform="matrix(.421875 0 0 .421875 0 0)">
      <path d="m320 0 320 320-320 320-320-320z" fill="url(#a)" />
      <path d="m441.5 352.4-121.5 287.6 320-320-83.1-83.2z" fill="url(#b)" />
    </g>
  </svg>
);

// NAB (National Australia Bank) SVG logo - icon only
const NABLogo = ({
  className,
  style
}: {
  className?: string;
  style?: React.CSSProperties;
}) => (
  <svg
    viewBox="0 0 30 30"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    style={style}
  >
    <defs>
      <linearGradient
        x1="-.61%"
        y1="50.05%"
        x2="99.92%"
        y2="50.05%"
        id="nab-logo_svg__a"
      >
        <stop stopColor="#ED0000" offset="0%" />
        <stop stopColor="#E90000" offset="7.73%" />
        <stop stopColor="#D10000" offset="64.86%" />
        <stop stopColor="#C80000" offset="100%" />
      </linearGradient>
      <linearGradient
        x1="50.01%"
        y1="99.02%"
        x2="50.01%"
        y2=".46%"
        id="nab-logo_svg__b"
      >
        <stop stopColor="#A50000" stopOpacity="0" offset="0%" />
        <stop stopColor="#A50000" offset="100%" />
      </linearGradient>
    </defs>
    <g fill="none">
      <path
        d="M29.304 21.144l-4.965-6.219 4.965-6.219c.301-.374.15-.674-.3-.674h-1.882c-.075 0-.225.075-.3.15l-6.47 6.743h-.377l4.965-6.219c.301-.374.15-.674-.3-.674h-1.881c-.076 0-.226.075-.301.15l-6.47 6.743h-.377l5.417-6.743c.075-.075.075-.225.075-.3L19.223.39c-.076-.45-.452-.525-.753-.15l-4.965 6.219-7.148-3.447c-.451-.225-.677 0-.602.45l1.73 7.717L.34 14.625c-.452.225-.452.525 0 .75l7.147 3.446-1.73 7.717c-.076.45.15.675.601.45l7.148-3.447 4.965 6.22c.301.374.602.299.753-.15l1.73-7.718h7.975c.527-.075.677-.375.376-.75z"
        fill="#ED0000"
      />
      <path
        d="M29.304 21.144l-4.965-6.219 4.965-6.219c.301-.374.15-.674-.3-.674h-1.882c-.075 0-.225.075-.3.15l-6.47 6.743h-.377l4.965-6.219c.301-.374.15-.674-.3-.674h-1.881c-.076 0-.226.075-.301.15l-6.47 6.743h-.377V25.94l3.01 3.746c.3.375.602.3.752-.15l1.73-7.717h7.975c.377 0 .527-.3.226-.674z"
        fill="url(#nab-logo_svg__a)"
      />
      <path
        d="M29.304 8.781L24.34 15h-3.99l6.47-6.743c.076-.075.226-.15.301-.15h1.881c.452-.075.602.3.301.674zm-4.74-.749h-1.88c-.076 0-.226.075-.302.15l-6.47 6.743H19.9l4.965-6.219c.301-.3.15-.674-.3-.674z"
        fill="url(#nab-logo_svg__b)"
      />
    </g>
  </svg>
);

// Allianz SVG logo (icon only, without text)
const AllianzLogo = ({
  className,
  style
}: {
  className?: string;
  style?: React.CSSProperties;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="300 0 100 100"
    className={className}
    style={style}
  >
    <path
      d="M357.34,77.39V22.68c0-4.36-1.55-5.83-5.78-5.83H338.75V23h.77c2.92,0,3.49.66,3.49,4.18V77.39Zm6.32,0h14.22V34.78c0-4.28-1.65-5.82-5.82-5.82h-8.4Zm-26.93,0V29H328.3c-4.17,0-5.8,1.54-5.8,5.82V77.39Zm54.61-27.81c0,24.11-17.34,41.64-41.15,41.64S309,73.69,309,49.58,326.37,7.94,350.19,7.94s41.15,17.63,41.15,41.64m8.42,0C399.76,21.23,378.55,0,350.19,0S300.6,21.23,300.6,49.58s21.21,49.57,49.59,49.57S399.76,78,399.76,49.58"
      fill="#003781"
    />
  </svg>
);

// Australian Super SVG logo
const AustralianSuperLogo = ({
  className,
  style
}: {
  className?: string;
  style?: React.CSSProperties;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="64.71906280517578 12.553157806396484 14.1961669921875 11.053028106689453"
    className={className}
    style={style}
  >
    <g>
      <g>
        <path
          fill="#00A9CE"
          d="M66.277,16.627c-0.14,0.016-0.24,0.143-0.224,0.283c0.012,0.104,0.087,0.191,0.189,0.217l6.39,1.475c0.5-1.195,0.5-2.255-0.345-2.5L66.277,16.627z"
        />
      </g>
      <g>
        <path
          fill="#00A9CE"
          d="M77.922,15.602l-5.63,0.5c0.855,0.26,0.845,1.315,0.345,2.5l-1.795,3.85c-0.066,0.124-0.019,0.279,0.105,0.345c0.104,0.055,0.231,0.032,0.31-0.055l6.865-6.705c0.102-0.097,0.106-0.258,0.01-0.36c-0.049-0.052-0.118-0.081-0.19-0.08l0,0"
        />
      </g>
    </g>
  </svg>
);

// Map institution names to their logo components
const institutionLogos: Record<
  string,
  React.FC<{ className?: string; style?: React.CSSProperties }>
> = {
  commonwealth: CommBankLogo,
  nab: NABLogo,
  'national australia bank': NABLogo,
  allianz: AllianzLogo,
  'australian super': AustralianSuperLogo,
  australiansuper: AustralianSuperLogo
  // Add more institutions as needed
  // 'westpac': WestpacLogo,
  // 'anz': ANZLogo,
};

export function InstitutionLogo({
  name,
  faviconUrl,
  className,
  size = 'md'
}: InstitutionLogoProps) {
  const { height } = sizeConfig[size];

  // If we have a favicon URL, use it
  if (faviconUrl) {
    return (
      <img
        src={faviconUrl}
        alt={`${name} logo`}
        className={cn('shrink-0 object-contain', className)}
        style={{
          height: `${height}px`,
          width: `${height}px`
        }}
        onError={(e) => {
          // Hide image if it fails to load
          e.currentTarget.style.display = 'none';
        }}
      />
    );
  }

  // Find matching logo component as fallback
  const lowerName = name.toLowerCase();
  const logoEntry = Object.entries(institutionLogos).find(([key]) =>
    lowerName.includes(key)
  );

  if (!logoEntry) {
    return null;
  }

  return React.createElement(logoEntry[1], {
    className: cn('shrink-0', className),
    style: {
      height: `${height}px`,
      width: 'auto'
    }
  });
}
